import * as React from 'react';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { TxKeyPath } from '@/core';
import { Text } from '@/ui';

import { Icon } from '../icon';

export const formatCountBadge = (count = 0) => {
  if (count <= 0) {
    return undefined;
  }

  return count >= 10 ? '9+' : String(count);
};

interface MenuItemProps {
  style?: any;
  leftIcon?: React.ReactNode;
  leftIconFillColor?: string;
  leftIconStyle?: any;
  rightIcon?: React.ReactNode;
  rightIconFillColor?: string;
  tx: TxKeyPath;
  tx2?: TxKeyPath;
  tx3?: TxKeyPath;
  txStyle?: any;
  tx2Style?: any;
  tx3Style?: any;
  onPress: (e: React.MouseEvent | React.TouchEvent) => void;
  values?: any;
  values2?: any;
  values3?: any;
  disabled?: boolean;
  active?: boolean;
  leftIconSize?: number;
  leftIconType?: 'dressed' | 'naked';
  badgeCount?: number;
}

export const MenuItem = ({
  style,
  leftIcon,
  leftIconFillColor,
  leftIconStyle,
  rightIcon,
  rightIconFillColor,
  tx,
  txStyle,
  tx2,
  tx3,
  tx2Style,
  tx3Style,
  onPress,
  values,
  values2,
  values3,
  disabled = false,
  active = false,
  leftIconSize = 25,
  leftIconType = 'dressed',
  badgeCount,
}: MenuItemProps) => {
  const { styles } = useStyles(stylesheet);
  const badgeLabel = formatCountBadge(badgeCount);

  const getContainerStyle = React.useMemo(() => {
    let stateStyle = styles.default;

    if (active) {
      stateStyle = styles.active;
    }

    return stateStyle;
  }, [active, styles]);

  return (
    <Pressable
      hitSlop={8}
      onPress={onPress}
      style={({ pressed }) => [
        styles.container,
        getContainerStyle,
        style,
        { opacity: pressed ? 0.5 : 1 },
      ]}
      disabled={disabled}
      pointerEvents={disabled ? 'none' : 'auto'}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          flex: 1,
          minWidth: 0,
        }}
      >
        {leftIcon ? (
          <Icon
            type={leftIconType}
            icon={leftIcon}
            width={leftIconSize}
            height={leftIconSize}
            fillColor={leftIconFillColor}
            style={leftIconStyle}
          />
        ) : null}

        <View>
          <Text
            tx={tx}
            type="bodyRegular"
            values={values}
            style={txStyle}
            numberOfLines={1}
          />

          {tx2 && (
            <Text tx={tx2} type="caption1" values={values2} style={tx2Style} />
          )}

          {tx3 && (
            <Text tx={tx3} type="caption1" values={values3} style={tx3Style} />
          )}
        </View>
      </View>

      <View style={styles.trailing}>
        {badgeLabel ? (
          <View style={styles.badge}>
            <Text
              type="caption1Bold"
              style={styles.badgeText}
              numberOfLines={1}
            >
              {badgeLabel}
            </Text>
          </View>
        ) : null}

        {rightIcon ? (
          <Icon
            type="naked"
            icon={rightIcon}
            width={12}
            height={12}
            fillColor={rightIconFillColor}
          />
        ) : null}
      </View>
    </Pressable>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    borderRadius: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    width: {
      md: 300,
    },
  },
  default: {
    backgroundColor: theme.colors.background_2,
  },
  active: {
    backgroundColor: theme.colors.card.hover.backgroundColor,
  },
  trailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  badge: {
    backgroundColor: theme.colors.button.primary.default.fill,
    borderRadius: 100,
    minWidth: 24,
    height: 24,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  badgeText: {
    color: theme.colors.button.primary.default.text,
    textAlign: 'center',
  },
}));
